import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import './AboutPage.css';

const AboutPage = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero" data-testid="about-hero">
        <div className="about-hero-content">
          <h1>Learning that leads to living well and leading change.</h1>
          <p className="about-hero-subtitle">
            We empower young people to communicate clearly, think critically, and take meaningful 
            action for a sustainable future.
          </p>
        </div>
      </section>

      {/* Who We Are */}
      <section className="section">
        <div className="content-wrapper">
          <h2 className="section-title">Who We Are</h2>
          <div className="who-we-are-content">
            <p className="intro-text">
              Litvill Lessons Association is a forward-thinking non-profit EdTech initiative born in 
              India with a simple belief: When young people learn to express themselves, understand 
              their world, and act with purpose, they can transform the future.
            </p>
            <p className="intro-text">
              <strong>Our Mission:</strong> We teach English and sustainability to underprivileged youth and 
              slum children across India, providing them with free education, mentorship, and pathways to 
              social entrepreneurship.
            </p>
            <p className="intro-text">
              <strong>Our Model:</strong> We partner with universities, engaging student volunteers as peer 
              tutors and professors as program leaders. This creates a powerful ecosystem where everyone 
              benefits—students gain invaluable experience, professors shape future leaders, and universities 
              build prestige through social impact.
            </p>
            <p className="intro-text">
              We built Litvill around a three-part journey:
            </p>
            <div className="journey-pillars">
              <div className="pillar">
                <span className="pillar-label learn">Learn English</span>
                <span className="pillar-desc">to find your voice</span>
              </div>
              <div className="pillar">
                <span className="pillar-label live">Live Sustainably</span>
                <span className="pillar-desc">to understand reality</span>
              </div>
              <div className="pillar">
                <span className="pillar-label lead">Lead Change</span>
                <span className="pillar-desc">to improve your community</span>
              </div>
            </div>
            <p className="intro-text">
              These are not separate programs but a single pathway where every skill feeds the next.
            </p>
            <p className="intro-text emphasis">
              Litvill rejects the idea of students as profit sources. We reinvest everything we can 
              into scholarships, mentorship, IELTS support, and real-world impact projects. Our mission 
              is to make learning accessible, human, and relevant.
            </p>
          </div>
        </div>
      </section>

      {/* Community Engagement Image */}
      <section className="section community-image-section">
        <div className="content-wrapper">
          <div className="community-image-container">
            <img 
              src="https://customer-assets.emergentagent.com/job_nonprofit-edu/artifacts/u0lnrvuo_talk.png"
              alt="Litvill community engagement and outreach"
              className="community-image"
            />
            <p className="image-caption">
              Engaging with communities and sharing the vision of accessible, sustainable education
            </p>
          </div>
        </div>
      </section>

      {/* Our Pathway */}
      <section className="section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">Our Pathway</h2>
          
          <div className="pathway-details">
            <div className="pathway-detail">
              <h3 className="pathway-detail-title" style={{ color: 'var(--primary)' }}>Learn English</h3>
              <p>
                We teach English as a tool for expression, collaboration, and confidence, not rote grammar. 
                Students learn through stories, short lessons, and real-world topics that prepare them to talk 
                about the issues that matter.
              </p>
            </div>

            <div className="pathway-detail">
              <h3 className="pathway-detail-title" style={{ color: 'var(--accent)' }}>Live Sustainably</h3>
              <p>
                Once learners gain confidence, they explore programs on water, food, health, survival skills, 
                and understanding themselves and others. Sustainability becomes personal, practical, and 
                action-oriented.
              </p>
            </div>

            <div className="pathway-detail">
              <h3 className="pathway-detail-title" style={{ color: 'var(--highlight)' }}>Lead Change</h3>
              <p>
                Our Social Entrepreneurship Movement connects universities, mentors, and students to build 
                projects that improve communities. It's the moment where knowledge becomes real impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Beliefs */}
      <section className="section">
        <div className="content-wrapper">
          <h2 className="section-title">Our Beliefs</h2>
          <div className="beliefs-grid">
            <div className="belief-card">
              <h4>Education is a public good.</h4>
              <p>Learning should empower, not exploit.</p>
            </div>
            <div className="belief-card">
              <h4>Sustainability starts with awareness.</h4>
              <p>Small, everyday actions shape long-term resilience.</p>
            </div>
            <div className="belief-card">
              <h4>Everyone can lead.</h4>
              <p>Leadership begins with communication, curiosity, and courage.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">Our Commitment</h2>
          <p className="intro-text">We channel all surpluses back into the community:</p>
          <ul className="commitment-list">
            <li>Scholarships for deserving students</li>
            <li>Support for IELTS preparation</li>
            <li>Research and pilot initiatives</li>
            <li>Tools that help learners and mentors grow</li>
          </ul>
          <p className="intro-text emphasis">
            Everything exists to strengthen India's progress toward a more inclusive and sustainable future.
          </p>
        </div>
      </section>

      {/* Our Collaborators */}
      <section className="section">
        <div className="content-wrapper">
          <h2 className="section-title">Our Collaborators</h2>
          <p className="intro-text">
            We work with forward-thinking universities and organizations who believe in youth leadership 
            and sustainable development.
          </p>
          <div className="collaborators-cta">
            <Link to="/#partners-section" className="btn btn-secondary">
              See our partners
            </Link>
          </div>
        </div>
      </section>

      {/* In the Media */}
      <section className="section media-section" style={{ backgroundColor: 'var(--light-grey)' }}>
        <div className="content-wrapper">
          <h2 className="section-title">In the Media</h2>
          <p className="intro-text">Stories featuring Litvill's founder, programs, and impact.</p>
          
          <div className="media-grid">
            {/* TycoonStory */}
            <div className="media-card" data-testid="media-tycoonstory">
              <div className="media-logo-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/ir1a5atv_tycoonstory.png" 
                  alt="TycoonStory" 
                  className="media-logo"
                />
              </div>
              <h3 className="media-title">
                "A Nonprofit Company Has Revolutionized The Way of Learning English Through Its 60-30-10 Formula!"
              </h3>
              <p className="media-date">November 28, 2022</p>
              <p className="media-description">
                This feature explores how Litvill Lessons Association applies a unique 60-30-10 model 
                (60% self-learning, 30% peer tutoring, 10% foreign mentors) to transform English learning 
                for under-served Indian students.
              </p>
              <a 
                href="https://www.tycoonstory.com/litvill-lessons-a-nonprofit-company-has-revolutionized-the-way-of-learning-english-through-its-60-20-20-formula/?utm_source=chatgpt.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="media-link"
              >
                Read article <ExternalLink size={16} />
              </a>
            </div>

            {/* YourStory */}
            <div className="media-card" data-testid="media-yourstory">
              <div className="media-logo-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/s5ydb13b_yourstory.png" 
                  alt="YourStory" 
                  className="media-logo"
                />
              </div>
              <h3 className="media-title">
                "Litvill Lessons is the only non-profit platform where you have a chance to earn some pocket money while you learn."
              </h3>
              <p className="media-description">
                This company profile highlights Litvill's unique approach—combining self-paced English learning 
                with peer tutoring and mentor-led sessions, all within a nonprofit model.
              </p>
              <a 
                href="https://yourstory.com/companies/litvill-lessons?utm_source=chatgpt.com" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="media-link"
              >
                Read article <ExternalLink size={16} />
              </a>
            </div>

            {/* The Startup Lab */}
            <div className="media-card" data-testid="media-startuplab">
              <div className="media-logo-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/zyvzttyp_startuplab.png" 
                  alt="The Startup Lab" 
                  className="media-logo"
                />
              </div>
              <h3 className="media-title">
                "Litvill Lessons is transforming the way school-going kids learn English by using 60-30-10 formula."
              </h3>
              <p className="media-description">
                This piece profiles how Litvill's 60-30-10 approach (60% self-study, 30% peer tutoring, 
                10% mentor webinars) is making English learning more engaging and effective for Indian students.
              </p>
              <a 
                href="https://thestartuplab.in/litvill-lessons-is-transforming-the-way-school-going-kids-learn-english-by-using-60-30-10-formula/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="media-link"
              >
                Read article <ExternalLink size={16} />
              </a>
            </div>

            {/* South India Review */}
            <div className="media-card" data-testid="media-southindia">
              <div className="media-logo-container">
                <img 
                  src="https://customer-assets.emergentagent.com/job_spec-evaluator/artifacts/ou7y8cbx_southindia.png" 
                  alt="South India Review" 
                  className="media-logo"
                />
              </div>
              <h3 className="media-title">
                "Visionary Organizations to Look for in 2025: Litvill Lessons Association & Chitkara University"
              </h3>
              <p className="media-description">
                This feature highlights how the collaboration between Litvill Lessons Association and Chitkara 
                University is empowering youth through sustainable education and social entrepreneurship, 
                positioning both organisations as pioneers in the evolving education-for-impact space.
              </p>
              <a 
                href="https://magazine.southindiareview.com/visionary-organizations-to-look-for-in-2025-litvill-lessons-association-chitkara-university-october2025/#page=1" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="media-link"
              >
                Read article <ExternalLink size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
